// site.js — page-level interactions around the helix experience:
// custom cursor, magnetic buttons, scroll reveals, nav show/hide + dark-hero
// flip, and the work-list hover preview. (The About section's own motion now
// lives in about.js.)
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const fine    = matchMedia('(pointer: fine)').matches;
const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;

export function initSite() {
  initReveals();
  initNav();
  if (fine && !reduced) {
    initCursor();
    initMagnetic();
  }
}

/* ── scroll reveals — CSS does the motion, we just flip a class ───────── */
function initReveals() {
  const els = document.querySelectorAll('[data-reveal]');
  if (reduced || !('IntersectionObserver' in window)) {
    els.forEach((el) => el.classList.add('is-inview'));
    return;
  }
  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-inview');
        io.unobserve(entry.target);
      }
    });
  }, { threshold: 0.18, rootMargin: '0px 0px -8% 0px' });
  els.forEach((el) => io.observe(el));
}

/* ── nav: hide scrolling down / reveal up, + light flip over dark hero ── */
function initNav() {
  const nav  = document.querySelector('[data-nav]');
  const hint = document.querySelector('[data-scroll-hint]');
  const hero = document.querySelector('[data-hero]');
  if (!nav) return;

  let lastY = window.scrollY;
  window.addEventListener('scroll', () => {
    const y = window.scrollY;
    nav.classList.toggle('is-hidden', y > 140 && y > lastY && !document.body.classList.contains('menu-open'));
    if (hint) hint.style.opacity = y > 60 ? '0' : '';
    lastY = y;
  }, { passive: true });

  // Flip the nav to light whenever a dark section occupies the top strip.
  // The hero is sticky (tie its tone to the initial scroll range) while the
  // folio is a pinned black stage (detect it physically covering the nav).
  const darkSections = [...document.querySelectorAll('.helixArea, .folioArea, .serviceArea')];
  const NAV_PROBE = 46;   // px from the top where the nav bar sits
  const syncNavTone = () => {
    let dark = hero ? window.scrollY < hero.offsetHeight - 1 : false;
    if (!dark) {
      for (const el of darkSections) {
        const r = el.getBoundingClientRect();
        if (r.top <= NAV_PROBE && r.bottom > NAV_PROBE) { dark = true; break; }
      }
    }
    nav.classList.toggle('is-onDark', dark);
  };
  window.addEventListener('scroll', syncNavTone, { passive: true });
  window.addEventListener('resize', syncNavTone);
  syncNavTone();
}

/* ── custom cursor — dot snaps, ring trails with a lerp ───────────────── */
function initCursor() {
  const root = document.querySelector('[data-cursor]');
  if (!root) return;
  const dot  = root.querySelector('.cursor__dot');
  const ring = root.querySelector('.cursor__ring');

  const pos = { x: innerWidth / 2, y: innerHeight / 2 };
  const trail = { x: pos.x, y: pos.y };

  window.addEventListener('pointermove', (e) => { pos.x = e.clientX; pos.y = e.clientY; }, { passive: true });

  gsap.ticker.add(() => {
    trail.x += (pos.x - trail.x) * 0.16;
    trail.y += (pos.y - trail.y) * 0.16;
    dot.style.transform  = `translate3d(${pos.x}px, ${pos.y}px, 0)`;
    ring.style.transform = `translate3d(${trail.x}px, ${trail.y}px, 0)`;
  });

  const HOVER = 'a, button, [data-magnetic], .serviceRow, [data-hero]';
  const VIEW  = '[data-service-img]';
  document.addEventListener('pointerover', (e) => {
    root.classList.toggle('is-hover', Boolean(e.target.closest(HOVER)));
    root.classList.toggle('is-view', Boolean(e.target.closest(VIEW)));
  });
}

/* ── magnetic pull on pill buttons / key links ────────────────────────── */
function initMagnetic() {
  document.querySelectorAll('[data-magnetic]').forEach((el) => {
    const strength = 0.35;
    el.addEventListener('pointermove', (e) => {
      const r = el.getBoundingClientRect();
      const x = (e.clientX - r.left - r.width / 2) * strength;
      const y = (e.clientY - r.top - r.height / 2) * strength;
      gsap.to(el, { x, y, duration: 0.4, ease: 'power3.out' });
    });
    el.addEventListener('pointerleave', () => {
      gsap.to(el, { x: 0, y: 0, duration: 0.7, ease: 'elastic.out(1, 0.45)' });
    });
  });
}

/* ── work list — floating image preview that follows the cursor ───────── */
function initWorkPreview() {
  const list = document.querySelector('[data-work-list]');
  const box  = document.querySelector('[data-preview-box]');
  const img  = document.querySelector('[data-preview-img]');
  if (!list || !box || !img) return;

  const move = (e) => {
    gsap.to(box, {
      x: e.clientX + 28,
      y: e.clientY - box.offsetHeight / 2,
      duration: 0.55,
      ease: 'power3.out',
    });
  };

  list.addEventListener('pointermove', move, { passive: true });
  list.querySelectorAll('[data-preview]').forEach((item) => {
    item.addEventListener('pointerenter', () => {
      img.src = item.dataset.preview;
      box.classList.add('is-on');
    });
  });
  list.addEventListener('pointerleave', () => box.classList.remove('is-on'));
}
